import { useState, useEffect } from 'react';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import NotAuthorized from '../../components/admin/NotAuthorized';
import DestructiveActionConfirm from '../../components/admin/DestructiveActionConfirm';
import { listAdminApiKeys, createAdminApiKey, revokeAdminApiKey, rotateAdminApiKey } from '../../services/adminApiKeysService';
import { createIntent } from '../../services/intentService';

export default function AdminApiKeysPage() {
  const { has } = useCapabilities();
  const canRead = has('admin:api-keys:read');
  const canManage = has('admin:api-keys:manage');

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  
  // Create modal state
  const [createModal, setCreateModal] = useState({
    open: false,
    name: '',
    reason: '',
    loading: false,
    error: null,
  });

  // Revealed token state (one-time only)
  const [revealedToken, setRevealedToken] = useState(null);

  // Revoke/Rotate modal state
  const [actionModal, setActionModal] = useState({
    open: false,
    action: null, // 'revoke' or 'rotate'
    keyId: null,
    keyName: null,
    reason: '',
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (canRead) {
      loadKeys();
    } else {
      setLoading(false);
    }
  }, [canRead, includeRevoked]);

  const loadKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listAdminApiKeys(includeRevoked);
      setKeys(response.keys || []);
    } catch (err) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCreateModal({
      open: true,
      name: '',
      reason: '',
      loading: false,
      error: null,
    });
    setRevealedToken(null);
  };

  const handleCreateConfirm = async (reason) => {
    const { name } = createModal;
    if (!name.trim()) {
      setCreateModal(prev => ({ ...prev, error: 'Name is required' }));
      return;
    }
    if (!reason || !reason.trim()) {
      setCreateModal(prev => ({ ...prev, error: 'Reason is required' }));
      return;
    }

    setCreateModal(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Create intent
      const intentResponse = await createIntent({
        action: 'admin_api_key.create',
        targetType: 'admin_api_key',
        targetId: 'new',
        reason: reason.trim(),
      });

      // Create key
      const response = await createAdminApiKey(name.trim(), reason.trim(), intentResponse.intentAuditEventId);
      const key = response.key;

      // Show token one-time
      setRevealedToken({
        token: key.token,
        name: key.name,
      });

      // Close modal and reload
      setCreateModal({ open: false, name: '', reason: '', loading: false, error: null });
      await loadKeys();
    } catch (err) {
      setCreateModal(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to create API key',
      }));
    }
  };

  const handleRevoke = (key) => {
    setActionModal({
      open: true,
      action: 'revoke',
      keyId: key.id,
      keyName: key.name,
      reason: '',
      loading: false,
      error: null,
    });
    setRevealedToken(null);
  };

  const handleRotate = (key) => {
    setActionModal({
      open: true,
      action: 'rotate',
      keyId: key.id,
      keyName: key.name,
      reason: '',
      loading: false,
      error: null,
    });
    setRevealedToken(null);
  };

  const handleActionConfirm = async () => {
    const { action, keyId, reason } = actionModal;
    if (!reason.trim()) {
      setActionModal(prev => ({ ...prev, error: 'Reason is required' }));
      return;
    }

    setActionModal(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Create intent
      const intentAction = action === 'revoke' ? 'admin_api_key.revoke' : 'admin_api_key.rotate';
      const intentResponse = await createIntent({
        action: intentAction,
        targetType: 'admin_api_key',
        targetId: keyId,
        reason: reason.trim(),
      });

      let response;
      if (action === 'revoke') {
        response = await revokeAdminApiKey(keyId, reason.trim(), intentResponse.intentAuditEventId);
      } else {
        response = await rotateAdminApiKey(keyId, null, reason.trim(), intentResponse.intentAuditEventId);
        if (response.new_key && response.new_key.token) {
          // Show new token one-time for rotation
          setRevealedToken({
            token: response.new_key.token,
            name: response.new_key.name,
          });
        }
      }

      // Close modal and reload
      setActionModal({ open: false, action: null, keyId: null, keyName: null, reason: '', loading: false, error: null });
      await loadKeys();
    } catch (err) {
      setActionModal(prev => ({
        ...prev,
        loading: false,
        error: err.message || `Failed to ${action} API key`,
      }));
    }
  };

  if (!canRead) {
    return <NotAuthorized requiredCapability="admin:api-keys:read" />;
  }

  if (loading) {
    return <div style={styles.container}>Loading API keys...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>API Keys</h1>
        {canManage && (
          <button onClick={handleCreate} style={styles.createButton} type="button">
            Create API Key
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error} role="alert">
          <p>Error: {error}</p>
          <button onClick={loadKeys} style={styles.button} type="button">
            Retry
          </button>
        </div>
      )}

      {revealedToken && (
        <div style={styles.tokenReveal}>
          <h3 style={styles.tokenTitle}>API Key Created</h3>
          <p style={styles.tokenWarning}>
            <strong>⚠️ This key will not be shown again. Copy it now.</strong>
          </p>
          <div style={styles.tokenContainer}>
            <code style={styles.token}>{revealedToken.token}</code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(revealedToken.token);
                alert('Token copied to clipboard');
              }}
              style={styles.copyButton}
              type="button"
            >
              Copy
            </button>
          </div>
          <button
            onClick={() => setRevealedToken(null)}
            style={styles.dismissButton}
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      <div style={styles.filters}>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
            style={styles.checkbox}
          />
          Include revoked keys
        </label>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Prefix</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Created At</th>
              <th style={styles.th}>Created By</th>
              <th style={styles.th}>Revoked At</th>
              <th style={styles.th}>Last Used</th>
              {canManage && <th style={styles.th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 8 : 7} style={styles.td}>
                  No API keys found
                </td>
              </tr>
            ) : (
              keys.map((key) => (
                <tr key={key.id}>
                  <td style={styles.td}>{key.name}</td>
                  <td style={styles.td}>
                    <code style={styles.code}>{key.token_prefix}...</code>
                  </td>
                  <td style={styles.td}>
                    {key.revoked_at ? (
                      <span style={styles.badgeRevoked}>Revoked</span>
                    ) : (
                      <span style={styles.badgeActive}>Active</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {new Date(key.created_at).toLocaleString()}
                  </td>
                  <td style={styles.td}>{key.created_by}</td>
                  <td style={styles.td}>
                    {key.revoked_at ? new Date(key.revoked_at).toLocaleString() : '—'}
                  </td>
                  <td style={styles.td}>
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : '—'}
                  </td>
                  {canManage && (
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        {!key.revoked_at && (
                          <>
                            <button
                              onClick={() => handleRevoke(key)}
                              style={{ ...styles.button, ...styles.revokeButton }}
                              type="button"
                            >
                              Revoke
                            </button>
                            <button
                              onClick={() => handleRotate(key)}
                              style={{ ...styles.button, ...styles.rotateButton }}
                              type="button"
                            >
                              Rotate
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal - Custom */}
      {createModal.open && (
        <div style={styles.modalOverlay} onClick={() => !createModal.loading && setCreateModal({ open: false, name: '', reason: '', loading: false, error: null })}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Create API Key</h2>
              {!createModal.loading && (
                <button
                  onClick={() => setCreateModal({ open: false, name: '', reason: '', loading: false, error: null })}
                  style={styles.modalClose}
                  type="button"
                >
                  ×
                </button>
              )}
            </div>
            <div style={styles.modalContent}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={createModal.name}
                  onChange={(e) => setCreateModal(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Production API Key"
                  style={styles.input}
                  disabled={createModal.loading}
                />
              </div>
              {createModal.error && (
                <div style={styles.error} role="alert">
                  {createModal.error}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setCreateModal({ open: false, name: '', reason: '', loading: false, error: null })}
                disabled={createModal.loading}
                style={styles.cancelButton}
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (createModal.name.trim()) {
                    // Open reason modal
                    setActionModal({
                      open: true,
                      action: 'create',
                      keyId: null,
                      keyName: createModal.name,
                      reason: '',
                      loading: false,
                      error: null,
                    });
                  } else {
                    setCreateModal(prev => ({ ...prev, error: 'Name is required' }));
                  }
                }}
                disabled={createModal.loading || !createModal.name.trim()}
                style={{
                  ...styles.createButton,
                  ...((createModal.loading || !createModal.name.trim()) && styles.buttonDisabled),
                  backgroundColor: '#10b981',
                  color: '#fff',
                }}
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal for Create */}
      <DestructiveActionConfirm
        open={actionModal.open && actionModal.action === 'create'}
        onClose={() => {
          if (!actionModal.loading) {
            setActionModal({ open: false, action: null, keyId: null, keyName: null, reason: '', loading: false, error: null });
          }
        }}
        onConfirm={handleCreateConfirm}
        title="Create API Key - Reason Required"
        description={`Create API key "${actionModal.keyName}"?`}
        confirmLabel="Create"
        loading={actionModal.loading}
        error={actionModal.error}
      />

      {/* Revoke/Rotate Modal */}
      <DestructiveActionConfirm
        open={actionModal.open && (actionModal.action === 'revoke' || actionModal.action === 'rotate')}
        onClose={() => {
          if (!actionModal.loading) {
            setActionModal({ open: false, action: null, keyId: null, keyName: null, reason: '', loading: false, error: null });
          }
        }}
        onConfirm={handleActionConfirm}
        title={actionModal.action === 'revoke' ? 'Revoke API Key' : 'Rotate API Key'}
        description={`${actionModal.action === 'revoke' ? 'Revoke' : 'Rotate'} API key "${actionModal.keyName}"?`}
        confirmLabel={actionModal.action === 'revoke' ? 'Revoke' : 'Rotate'}
        loading={actionModal.loading}
        error={actionModal.error}
      />
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    margin: 0,
    color: '#111827',
  },
  createButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  error: {
    padding: '1rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  },
  tokenReveal: {
    padding: '1.5rem',
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b',
    borderRadius: '0.5rem',
    marginBottom: '1.5rem',
  },
  tokenTitle: {
    marginTop: 0,
    marginBottom: '0.5rem',
    color: '#92400e',
  },
  tokenWarning: {
    marginBottom: '1rem',
    color: '#92400e',
    fontWeight: '500',
  },
  tokenContainer: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  token: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
  },
  copyButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
  },
  dismissButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#6b7280',
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
  },
  filters: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    backgroundColor: '#f3f4f6',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
  },
  td: {
    padding: '1rem',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb',
  },
  code: {
    backgroundColor: '#f3f4f6',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  badgeActive: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  badgeRevoked: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  button: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  revokeButton: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
  rotateButton: {
    backgroundColor: '#3b82f6',
    color: '#fff',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  textarea: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
    width: '90%',
    maxWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#111827',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
  },
  modalContent: {
    padding: '1.5rem',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    padding: '1.5rem',
    borderTop: '1px solid #e5e7eb',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  required: {
    color: '#ef4444',
  },
};

